import { useForm, useStore } from "@tanstack/react-form";
import styled from "styled-components";
import { Button } from "../../components/Button";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { Form } from "../../components/Form";
import { Toggle } from "../../components/Toggle";
import { InfoBox } from "../../components/InfoBox";
import { useTranslation } from "react-i18next";
import { useAddResellerUserMutation } from "../../query/usersQuery";
import type { ResellerId } from "@KassaroAB/shared/types/ids";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

interface ResellerUserFormProps {
  resellerId: ResellerId | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const ResellerUserForm = ({ resellerId, onSuccess, onCancel }: ResellerUserFormProps) => {
  const { t } = useTranslation();
  const {mutate: addUserMutation, isPending, error} = useAddResellerUserMutation(resellerId);

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
      isNewUser: false,
    },
    onSubmit: ({ value }) => {
      addUserMutation(
        {
          email: value.email,
          password: value.isNewUser ? value.password : undefined,
        },
        {
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
        }
      );
    },
  });

  const isNewUser = useStore(form.store, (state) => state.values.isNewUser);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {error && (
        <InfoBox variant="error">
          {error?.message ?? t("userForm.errorAdd")}
        </InfoBox>
      )}

      <form.Field name="isNewUser">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("userForm.createNewUser")}</FormLabel>
            <Toggle
              checked={field.state.value}
              onChange={(checked) => field.handleChange(checked)}
              onBlur={field.handleBlur}
              label={field.state.value ? t("userForm.toggleNewUser") : t("userForm.toggleExistingUser")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="email"
        validators={{
          onChange: ({ value }) => {
            if (!value) return t("userForm.emailRequired");
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) return t("userForm.emailInvalid");
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("userForm.emailLabel")}</FormLabel>
            <Input
              type="email"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("userForm.emailPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      {isNewUser && (
        <form.Field
          name="password"
          validators={{
            onChange: ({ value }) => {
              if (!value) {
                return t("userForm.passwordRequiredNew");
              }
              if (value && value.length < 6) {
                return t("userForm.passwordMinLength");
              }
              return undefined;
            },
          }}
        >
          {(field) => (
            <FormGroup>
              <FormLabel>{t("userForm.passwordLabel")}</FormLabel>
              <Input
                type="password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                required
                placeholder={t("userForm.passwordPlaceholder")}
              />
              {field.state.meta.errors.length > 0 && (
                <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
              )}
            </FormGroup>
          )}
        </form.Field>
      )}

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel} disabled={isPending}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? t("userForm.adding") : t("userForm.addUser")}
        </Button>
      </FormActions>
    </Form>
  );
};
